import * as XLSX from 'xlsx';
import { Measure } from '../utils/Measure.js';
import { withMeasurementDisplayOffset } from '../utils/SharedUtils.js';

function getI18n() {
  if (typeof i18n !== 'undefined') return i18n;
  if (typeof window !== 'undefined' && window.i18n) return window.i18n;
  return null;
}

function t(key, fallback) {
  const inst = getI18n();
  if (inst && typeof inst.t === 'function') return inst.t(key, fallback);
  return fallback;
}

function classifyMeasurement(m) {
  if (m.showAzimuth) return t('measurements.name_azimuth', 'Azimuth');
  if (m.showCircle) return t('measurements.name_circle', 'Circle');
  if (m.showHeight) return t('measurements.name_height', 'Height');
  if (m.maxMarkers === 1 || m.points.length === 1) return t('measurements.name_point', 'Point');
  if (m.showAngles && !m.showArea && !m.showDistances) return t('measurements.name_angle', 'Angle');
  if (m.showArea) return t('measurements.name_area', 'Area');
  if (m.showDistances) return t('measurements.name_distance', 'Distance');
  return t('measurements.name_measurement', 'Measurement');
}

function classifyKind(m) {
  if (m.showAzimuth) return 'azimuth';
  if (m.showCircle) return 'circle';
  if (m.showHeight) return 'height';
  if (m.maxMarkers === 1 || m.points.length === 1) return 'point';
  if (m.showAngles && !m.showArea && !m.showDistances) return 'angle';
  if (m.showArea) return 'area';
  if (m.showDistances) return 'distance';
  return 'measurement';
}

export class XLSXExporter {
  static toBlob(measurements, offset) {
    if (!(measurements instanceof Array)) {
      measurements = [measurements];
    }
    measurements = measurements.filter(m => m instanceof Measure);

    const L = {
      sheet_summary: t('measurements.xlsx.sheet_summary', 'Summary'),
      sheet_vertices: t('measurements.xlsx.sheet_vertices', 'Vertices'),
      col_name: t('measurements.xlsx.col_name', 'name'),
      col_type: t('measurements.xlsx.col_type', 'type'),
      col_points: t('measurements.xlsx.col_points', 'points'),
      col_closed: t('measurements.xlsx.col_closed', 'closed'),
      col_total_distance: t('measurements.xlsx.col_total_distance', 'total distance'),
      col_area: t('measurements.xlsx.col_area', 'area'),
      col_height: t('measurements.xlsx.col_height', 'height'),
      col_x: t('measurements.xlsx.col_x', 'x'),
      col_y: t('measurements.xlsx.col_y', 'y'),
      col_z: t('measurements.xlsx.col_z', 'z'),
      col_measurement_name: t('measurements.xlsx.col_measurement_name', 'measurement name'),
      col_vertex_index: t('measurements.xlsx.col_vertex_index', 'vertex index'),
      col_angle_rad: t('measurements.xlsx.col_angle_rad', 'angle (rad)'),
      yes: t('measurements.xlsx.yes', 'yes'),
      no: t('measurements.xlsx.no', 'no'),
    };

    const summaryRows = [];
    const vertexRows = [];

    for (const m of measurements) {
      const kind = classifyKind(m);
      const typeLabel = classifyMeasurement(m);
      const pointCount = m.points.length;
      const totalDistance = (kind === 'distance' || kind === 'area' || kind === 'circle')
        ? m.getTotalDistance() : '';
      const area = (kind === 'area' || kind === 'circle') ? m.getArea() : '';
      let height = '';
      if (kind === 'height' && pointCount >= 2) {
        height = Math.abs(m.points[1].position.y - m.points[0].position.y);
      }
      const single = (kind === 'point' && pointCount >= 1) ? m.points[0].position : null;
      const [sx, sy, sz] = single
        ? withMeasurementDisplayOffset(single, offset)
        : ['', '', ''];

      summaryRows.push({
        [L.col_name]: m.name || '',
        [L.col_type]: typeLabel,
        [L.col_points]: pointCount,
        [L.col_closed]: m.closed ? L.yes : L.no,
        [L.col_total_distance]: totalDistance,
        [L.col_area]: area,
        [L.col_height]: height,
        [L.col_x]: single ? sx : '',
        [L.col_y]: single ? sy : '',
        [L.col_z]: single ? sz : '',
      });

      for (let i = 0; i < m.points.length; i++) {
        const [vx, vy, vz] = withMeasurementDisplayOffset(m.points[i].position, offset);
        const row = {
          [L.col_measurement_name]: m.name || '',
          [L.col_type]: typeLabel,
          [L.col_vertex_index]: i,
          [L.col_x]: vx,
          [L.col_y]: vy,
          [L.col_z]: vz,
        };
        if (kind === 'angle') {
          row[L.col_angle_rad] = m.getAngle(i);
        }
        vertexRows.push(row);
      }
    }

    const wb = XLSX.utils.book_new();

    const summaryHeader = [
      L.col_name, L.col_type, L.col_points, L.col_closed,
      L.col_total_distance, L.col_area, L.col_height,
      L.col_x, L.col_y, L.col_z,
    ];
    const summarySheet = XLSX.utils.json_to_sheet(summaryRows, { header: summaryHeader });
    XLSX.utils.book_append_sheet(wb, summarySheet, L.sheet_summary);

    const vertexHeader = [
      L.col_measurement_name, L.col_type, L.col_vertex_index,
      L.col_x, L.col_y, L.col_z, L.col_angle_rad,
    ];
    const vertexSheet = XLSX.utils.json_to_sheet(vertexRows, { header: vertexHeader });
    XLSX.utils.book_append_sheet(wb, vertexSheet, L.sheet_vertices);

    const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    return new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }
}
